import useMountedRef from "../../utils/useMountedRef";
import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import ReturnIcon from "../../assets/icons/svg/ReturnIcon";
import ParentalSelectingIcon from "../../assets/icons/svg/ParentalSelectingIcon";
import UnselectIcon from "../../assets/icons/svg/UnselectIcon";
import { updateNotificationPreferences } from "../../api/settings/Settings";
import localizationStrings from "../../statics/localization";
import "./NotificationsSettings.scss";

const PreferenceCard = ({ title, enabled, onChange, disabled }) => {
  return (
    <div className="notification-pref-card">
      <div className="notification-pref-title">{title}</div>
      <div className="notification-pref-options">
        <button
          type="button"
          className={`notification-pref-option ${enabled ? "selected" : ""}`}
          aria-pressed={enabled}
          disabled={disabled}
          onClick={() => onChange(true)}
        >
          <span className="notification-pref-icon">
            {enabled ? <ParentalSelectingIcon /> : <UnselectIcon />}
          </span>
          <span className="notification-pref-label">
            {localizationStrings.notificationsPage.on}
          </span>
        </button>
        <button
          type="button"
          className={`notification-pref-option ${!enabled ? "selected" : ""}`}
          aria-pressed={!enabled}
          disabled={disabled}
          onClick={() => onChange(false)}
        >
          <span className="notification-pref-icon">
            {!enabled ? <ParentalSelectingIcon /> : <UnselectIcon />}
          </span>
          <span className="notification-pref-label">
            {localizationStrings.notificationsPage.off}
          </span>
        </button>
      </div>
    </div>
  );
};

const NotificationsSettings = () => {
  const history = useHistory();
  // Persist only confirmed values for this profile/session. Never store a token.
  const storageKey = `notification-preferences:${JSON.parse(localStorage.getItem("profile") || "null")?.id || "current"}`;
  const mounted = useMountedRef();
  const pending = useRef(false);
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey));
      if (typeof stored?.enableCampaignNotifications === "boolean" && typeof stored?.enableContentNotifications === "boolean") return stored;
    } catch (e) { /* Ignore invalid cached preferences. */ }
    return { enableCampaignNotifications: true, enableContentNotifications: false };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errorText, setErrorText] = useState("");

  const savePreference = async (nextPreferences, previousPreferences) => {
    setIsSaving(true);
    setErrorText("");

    try {
      const response = await updateNotificationPreferences(
        nextPreferences.enableCampaignNotifications,
        nextPreferences.enableContentNotifications
      );

      const isSucceeded =
        response?.data?.IsSucceeded ?? response?.data?.isSucceeded;

      if (isSucceeded === false) throw new Error("Notification preference update failed");
      sessionStorage.setItem(storageKey, JSON.stringify(nextPreferences));
    } catch (error) {
      if (mounted.current) {
        setPreferences(previousPreferences);
        setErrorText(localizationStrings.notificationsPage.updateError);
      }
    } finally {
      pending.current = false;
      if (mounted.current) setIsSaving(false);
    }
  };

  const updatePreferenceField = (key, value) => {
    if (pending.current) return;
    const previousPreferences = { ...preferences };
    const nextPreferences = {
      ...preferences,
      [key]: value
    };

    if (previousPreferences[key] === value) {
      return;
    }

    pending.current = true;
    setPreferences(nextPreferences);
    savePreference(nextPreferences, previousPreferences);
  };

  return (
    <div className="settings-container notification-settings-container">
      <div
        className="settings-icon"
        style={{
          position: "fixed",
          left: `${(33 / 1920) * 100}vw`,
          top: `calc(${(72 / 1920) * 100}vw + ${(49 / 1920) * 100}vw)`,
          zIndex: 5,
          margin: 0,
          gap: "10px"
        }}
        onClick={() => {
          history.push(`/dashboard/settings`);
        }}
      >
        <ReturnIcon />
        <div className="icon-settings">{localizationStrings.back}</div>
      </div>

      <div className="notification-settings-content">
        <div className="notification-settings-title">
          {localizationStrings.notificationsPage.title}
        </div>

        <PreferenceCard
          disabled={isSaving}
          title={localizationStrings.notificationsPage.campaign}
          enabled={preferences.enableCampaignNotifications}
          onChange={(value) =>
            updatePreferenceField("enableCampaignNotifications", value)
          }
        />

        <PreferenceCard
          disabled={isSaving}
          title={localizationStrings.notificationsPage.content}
          enabled={preferences.enableContentNotifications}
          onChange={(value) =>
            updatePreferenceField("enableContentNotifications", value)
          }
        />

        {isSaving ? (
          <div className="notification-settings-status">
            {localizationStrings.notificationsPage.saving}
          </div>
        ) : null}
        {errorText ? (
          <div role="alert" className="notification-settings-error">{errorText}</div>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationsSettings;